import React, { useState, useEffect, useRef } from 'react';
import { Platform, StatusBar, StyleSheet, TouchableOpacity, View } from 'react-native';
import { CameraView, useCameraPermissions } from 'expo-camera';
import Icon from '../components/Icon';

import { Color, CommonProperties, CommonStyles } from '../styles';
import BaseText from './BaseText';
import { scale } from '../utils/scale.utils';
import { useTranslation } from 'react-i18next';
import { useComponentTKey, i18nNS } from '../i18n';
import Button from './Button';
import Modal from './Modal';
import { pickImage, prependJpegMetadataImage } from '../utils/image.utils';

const Camera = ({ onClose, onGetImage, showGallery, showRotate, isExpense = false }) => {
  const { tKey } = useComponentTKey('Camera');
  const { t } = useTranslation(i18nNS.me);
  const cameraRef = useRef(null);
  const [facing, setFacing] = useState('back');
  const [permission, requestPermission] = useCameraPermissions();
  const isWeb = Platform.OS === 'web';

  useEffect(() => {
    requestPermission();
  }, []);

  if (!permission) {
    return (
      <Modal visible>
        <View style={[CommonStyles.flex1, CommonStyles.centerAlignment]}>
          <BaseText style={CommonStyles.h4}>{t(tKey('NoCameraAccess'))}</BaseText>
        </View>
      </Modal>
    );
  }

  if (!permission.granted) {
    return (
      <Modal visible>
        <View style={[CommonStyles.flex1, CommonStyles.centerAlignment]}>
          <BaseText style={CommonStyles.h4}>{t(tKey('NoCameraAccess'))}</BaseText>
          {!isWeb && (
            <Button
              label={'Grant Permission'}
              onPress={requestPermission}
              labelStyle={CommonStyles.h4}
              style={[CommonStyles.px5, CommonStyles.borderRadius4, CommonStyles.mt3]}
            />
          )}
          <Button
            label={'Go Back'}
            onPress={() => {
              StatusBar.setHidden(false);
              onClose();
            }}
            labelStyle={CommonStyles.h4}
            style={[CommonStyles.px5, CommonStyles.borderRadius4, CommonStyles.mt3]}
          />
        </View>
      </Modal>
    );
  }

  const takePicture = async () => {
    if (!cameraRef.current) {
      return;
    }
    const res = await cameraRef.current.takePictureAsync({ base64: true, skipProcessing: true });
    if (!isWeb && res.base64) {
      res.base64 = prependJpegMetadataImage(res.base64);
    }
    StatusBar.setHidden(false);
    onGetImage(res);
  };

  const onSelectImage = (result) => {
    StatusBar.setHidden(false);
    onGetImage(result?.assets?.[0]);
  };

  const closeCamera = () => {
    StatusBar.setHidden(false);
    onClose();
  };

  const cameraProps = isWeb ? { type: facing } : { facing };

  return (
    <Modal visible>
      <CameraView ref={cameraRef} ratio="16:9" style={CommonStyles.flex1} {...cameraProps}>
        <TouchableOpacity onPress={closeCamera} style={styles.close}>
          <Icon name="cancel" size={24} color="white" />
        </TouchableOpacity>
        <View style={styles.actions}>
          {showGallery ? (
            <TouchableOpacity style={styles.extraAction} onPress={() => pickImage(onSelectImage)}>
              <Icon name="image" color={Color.primary} size={24} />
            </TouchableOpacity>
          ) : (
            <View />
          )}
          <TouchableOpacity onPress={takePicture} style={styles.camera}>
            <Icon name="camera" color={Color.primary} size={36} />
          </TouchableOpacity>
          {showRotate ? (
            <TouchableOpacity
              style={styles.extraAction}
              onPress={() => setFacing(facing === 'back' ? 'front' : 'back')}
            >
              <Icon name="redo" color={Color.primary} size={24} />
            </TouchableOpacity>
          ) : (
            <View />
          )}
        </View>
      </CameraView>
    </Modal>
  );
};

const styles = StyleSheet.create({
  actions: {
    flex: 1,
    backgroundColor: Color.transparent,
    flexDirection: 'row',
    justifyContent: 'space-around',
    alignItems: 'flex-end',
    position: 'absolute',
    width: '100%',
    bottom: scale(24),
  },
  camera: {
    width: scale(72),
    height: scale(72),
    borderRadius: scale(36),
    borderColor: Color.accent,
    ...CommonProperties.centerAlignment,
    backgroundColor: Color.accent,
  },
  extraAction: {
    backgroundColor: Color.accent,
    ...CommonProperties.p3,
    ...CommonProperties.borderRadius1,
  },
  close: {
    position: 'absolute',
    ...CommonProperties.m5,
    top: 0,
    right: 0,
  },
});

export default Camera;
